import { Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Chat, ChatMessage, ChatMessageCreate, GetParams, GetResponse, PostResponse } from '@app/shared';
import { Observable, Subject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { convertGetParamsToHttpParams } from '../utils/api-utils';

@Injectable({
  providedIn: 'root'
})
export class CommunicationService {

  refreshNeeded = signal(0);

  /** URLs principales relacionadas con Chat **/
  chatUrl = environment.communicationApiBaseUrl + '/api/chat';
  chatMessageUrl = environment.communicationApiBaseUrl + '/api/chatMessage';
  chatMessageReadersUrl = environment.communicationApiBaseUrl + '/api/chatMessageReaders';
  notificationUrl = environment.communicationApiBaseUrl + '/api/notification';
  emailUrl = environment.communicationApiBaseUrl + '/api/email';

  constructor(private http: HttpClient) {
  }

  /** Metodos de obtener/crear/actualizar/eliminar Chat **/
  getChat(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.chatUrl, { params: convertGetParamsToHttpParams(getParams) });
  }

  createChat(newChat: string): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.chatUrl, newChat);
  }

  updateChat(Chat: Chat): Observable<any> {
    return this.http.put(this.chatUrl, Chat);
  }

  deleteChat(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatUrl, { body: { ids: desconsolidationsTypeIds } });
  }

  softDeleteChat(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatUrl + '/soft', { body: { ids: desconsolidationsTypeIds } });
  }

  /** Metodos de obtener/crear/actualizar/eliminar ChatMessage **/
  getChatMessage(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.chatMessageUrl, { params: convertGetParamsToHttpParams(getParams) });
  }

  createChatMessage(newChatMessage: ChatMessageCreate): Observable<PostResponse> {
    const formData = new FormData();

    // Campos obligatorios
    formData.append('message', newChatMessage.message);
    formData.append('type', newChatMessage.type);
    formData.append('chatId', newChatMessage.chatId.toString());
    formData.append('userId', newChatMessage.userId.toString());
    formData.append('authUserEmail', newChatMessage.authUserEmail);

    // Archivos opcionales
    if (newChatMessage.files) {
      newChatMessage.files.forEach((file) => {
        formData.append(`files`, file);
      });
    }

    // Headers necesarios
    const headers = new HttpHeaders()
      .set('Accept', 'application/json')

    return this.http.post<PostResponse>(this.chatMessageUrl, formData, { headers });
  }

  updateChatMessage(ChatMessage: ChatMessage): Observable<any> {
    return this.http.put(this.chatMessageUrl, ChatMessage);
  }

  deleteChatMessage(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatMessageUrl, { body: { ids: desconsolidationsTypeIds } });
  }

  softDeleteChatMessage(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatMessageUrl + '/soft', { body: { ids: desconsolidationsTypeIds } });
  }

  /** Metodos de obtener/crear/actualizar/eliminar Notification **/
  getNotification(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.notificationUrl, { params: convertGetParamsToHttpParams(getParams) });
  }

  createNotification(newNotification: string): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.notificationUrl, newNotification);
  }

  updateNotification(id: number, isRead: boolean): Observable<PostResponse> {
    return this.http.put<PostResponse>(this.notificationUrl, { id, isRead }).pipe(
      tap(() => this.updateBadge())
    );
  }
  updateBadge() {
    this.refreshNeeded.update(count => count + 1)
  }

  deleteNotification(notificationsIds: number[]): Observable<any> {
    return this.http.delete(this.notificationUrl, { body: { ids: notificationsIds } });
  }

  softDeleteNotification(notificationsIds: number[]): Observable<any> {
    return this.http.delete(this.notificationUrl + '/soft', { body: { ids: notificationsIds } });
  }

  getPendingNotification(userId: number): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.notificationUrl + '/pending', { params: { userId: userId } });
  }

  createMessageReader(chatMessageId: number, userId: number): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.chatMessageReadersUrl, { chatMessageId, userId })
  }

  sendEmail(emailData: {
    toEmailAddress: string;          // correos separados por ';' o ','
    subject: string;
    plainTextContent: string;
    htmlContent: string;
    toName?: string;
    fromName?: string;
    replyToEmailAddress?: string;
    replyToName?: string;
    attachments?: File[];
  }): Observable<PostResponse> {
    const formData = new FormData();

    formData.append('toEmailAddress', emailData.toEmailAddress);
    formData.append('subject', emailData.subject);

    if (emailData.fromName) {
      formData.append('fromName', emailData.fromName);
    }
    if (emailData.toName) {
      formData.append('toName', emailData.toName);
    }

    formData.append('plainTextContent', emailData.plainTextContent);
    formData.append('htmlContent', emailData.htmlContent);

    if (emailData.replyToEmailAddress) {
      formData.append('replyToEmailAddress', emailData.replyToEmailAddress);
    }
    if (emailData.replyToName) {
      formData.append('replyToName', emailData.replyToName);
    }

    if (emailData.attachments && emailData.attachments.length > 0) {
      if (emailData.attachments.length === 1) {
        formData.append('file', emailData.attachments[0]);
      } else {
        emailData.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }
    }

    const headers = new HttpHeaders()
      .set('Accept', 'application/json');

    return this.http.post<PostResponse>(this.emailUrl, formData, { headers });
  }

  /** Métodos para MailFollower **/
  mailFollowerUrl = environment.communicationApiBaseUrl + '/api/mailfollower';

  // Obtener seguidores de una entidad
  getMailFollowers(entityType: number, entityId: number): Observable<GetResponse> {
    const params = new HttpParams()
      .set('entityType', entityType.toString())
      .set('entityId', entityId.toString())
      .set('sortProperty', 'id')
      .set('sortDirection', 'asc')
      .set('startRow', '0')
      .set('endRow', '1000'); // Obtener hasta 1000 seguidores
    return this.http.get<GetResponse>(this.mailFollowerUrl, { params });
  }

  // Agregar un solo seguidor
  addMailFollower(payload: {
    entityType: number;
    entityId: number;
    userId?: number;
    partnerId?: number;
  }): Observable<GetResponse> {
    return this.http.post<GetResponse>(this.mailFollowerUrl, payload);
  }

  // Agregar múltiples seguidores
  addManyMailFollowers(payload: {
    entityType: number;
    entityId: number;
    userIds?: number[];
    partnerIds?: number[];
  }): Observable<GetResponse> {
    return this.http.post<GetResponse>(this.mailFollowerUrl + '/add-many', payload);
  }

  // Eliminar un seguidor (DELETE con body)
  deleteMailFollower(payload: {
    entityType: number;
    entityId: number;
    userId?: number;
    partnerId?: number;
  }): Observable<GetResponse> {
    return this.http.delete<GetResponse>(this.mailFollowerUrl, { body: payload });
  }
}
